import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Check, X, Sliders, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PACKAGE_DELIVERABLES,
  getBasePackageInfo,
  getBasePackageDeliverables,
  groupDeliverablesByCategory
} from "@/lib/package-config";
import { CATEGORY_CONFIG } from "@/lib/category-config";
import { usePlanStore } from "@/stores/plan-store";
import { useAuth } from "@/lib/auth";

interface ShowPackage {
  showId: number;
  setCore: boolean;
  setAudio: boolean;
  setVideo: boolean;
}

interface PlanDeliverablesProps {
  showPackages: Record<number, ShowPackage>;
}

// Transform deliverables from single source of truth (package-config.ts) into display format
// Filter to only standard deliverables (exclude optional add-ons which are shown in Custom packages)
const standardDeliverables = PACKAGE_DELIVERABLES.filter(item =>
  !item.category || item.category === 'standard'
);

const DELIVERABLES = {
  // SET Core: Show items that are in Core OR in higher tiers (for comparison)
  setCore: standardDeliverables
    .filter(item =>
      item.setCore.included || item.setAudio.included || item.setVideo.included
    )
    .map(item => ({
      name: item.name,
      included: item.setCore.included,
      note: item.setCore.note
    })),

  // SET Audio: Show items that are in Audio OR in Video (for comparison)
  setAudio: standardDeliverables
    .filter(item =>
      item.setAudio.included || item.setVideo.included
    )
    .map(item => ({
      name: item.name,
      included: item.setAudio.included,
      note: item.setAudio.note
    })),

  // SET Video: Show all standard deliverables (highest tier gets everything)
  setVideo: standardDeliverables.map(item => ({
    name: item.name,
    included: item.setVideo.included,
    note: item.setVideo.note
  }))
};

export function PlanDeliverables({ showPackages }: PlanDeliverablesProps) {
  const { user } = useAuth();
  const customPackage = usePlanStore(state => state.getCustomPackage(user?.id));

  // Count shows for each package type
  const packageCounts = Object.values(showPackages).reduce(
    (acc, pkg) => {
      if (pkg.setCore) acc.setCore++;
      if (pkg.setAudio) acc.setAudio++;
      if (pkg.setVideo) acc.setVideo++;
      return acc;
    },
    { setCore: 0, setAudio: 0, setVideo: 0 }
  );

  // Check if there are any custom package shows
  const hasCustomShows = Object.values(showPackages).some(pkg =>
    !pkg.setCore && !pkg.setAudio && !pkg.setVideo
  );

  const totalShows = Object.keys(showPackages).length;

  // Get SET Core deliverable names for custom package display
  const setCoreDeliverableNames = PACKAGE_DELIVERABLES
    .filter(item => item.setCore.included)
    .map(item => item.name);

  if (totalShows === 0) {
    return (
      <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Package Deliverables
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
            <Package className="h-12 w-12 mb-2 opacity-20" />
            <p>Select shows to see package deliverables</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Package Deliverables
          <span className="text-sm font-normal text-muted-foreground ml-2">
            (Included with selected shows)
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* SET Core Package */}
          <div
            className={cn(
              "rounded-xl overflow-hidden transition-all duration-300",
              packageCounts.setCore > 0
                ? "border-2 border-transparent bg-gradient-to-r from-gray-400 to-gray-500 p-[2px] shadow-lg hover:shadow-xl hover:scale-[1.02]"
                : "border-2 border-gray-200 bg-gray-50/30 opacity-60"
            )}
          >
            <div className={cn(
              "h-full rounded-[10px] overflow-hidden",
              packageCounts.setCore > 0 ? "bg-white" : "bg-gray-50/30"
            )}>
            <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">SET Core</h3>
                  <p className="text-sm opacity-90">Essential Foundation</p>
                </div>
                <div className="w-3 h-3 rounded-full bg-white"></div>
              </div>
              {packageCounts.setCore > 0 && (
                <div className="mt-2 text-sm font-medium">
                  {packageCounts.setCore} {packageCounts.setCore === 1 ? 'show' : 'shows'} selected
                </div>
              )}
            </div>

            <div className="p-4 space-y-3">
              {DELIVERABLES.setCore.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm hover:bg-gray-50/50 -mx-1 px-1 py-1 rounded transition-colors">
                  {item.included ? (
                    <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  ) : (
                    <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                  )}
                  <div className={cn("flex-1", !item.included && "text-gray-400")}>
                    <div className={item.included ? "font-medium" : ""}>{item.name}</div>
                    {item.note && (
                      <div className="text-xs text-gray-500 mt-0.5">{item.note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>

          {/* SET Audio Package */}
          <div
            className={cn(
              "rounded-xl overflow-hidden transition-all duration-300",
              packageCounts.setAudio > 0
                ? "border-2 border-transparent bg-gradient-to-r from-blue-400 to-blue-600 p-[2px] shadow-lg hover:shadow-xl hover:scale-[1.02]"
                : "border-2 border-gray-200 bg-gray-50/30 opacity-60"
            )}
          >
            <div className={cn(
              "h-full rounded-[10px] overflow-hidden",
              packageCounts.setAudio > 0 ? "bg-white" : "bg-gray-50/30"
            )}>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">SET Audio</h3>
                  <p className="text-sm opacity-90">Audio-Enhanced Experience</p>
                </div>
                <div className="w-3 h-3 rounded-full bg-white"></div>
              </div>
              {packageCounts.setAudio > 0 && (
                <div className="mt-2 text-sm font-medium">
                  {packageCounts.setAudio} {packageCounts.setAudio === 1 ? 'show' : 'shows'} selected
                </div>
              )}
            </div>

            <div className="p-4 space-y-3">
              {DELIVERABLES.setAudio.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm hover:bg-blue-50/50 -mx-1 px-1 py-1 rounded transition-colors">
                  {item.included ? (
                    <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  ) : (
                    <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                  )}
                  <div className={cn("flex-1", !item.included && "text-gray-400")}>
                    <div className={item.included ? "font-medium" : ""}>{item.name}</div>
                    {item.note && (
                      <div className="text-xs text-blue-600 mt-0.5 font-medium">{item.note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>

          {/* SET Video Package */}
          <div
            className={cn(
              "rounded-xl overflow-hidden transition-all duration-300",
              packageCounts.setVideo > 0
                ? "border-2 border-transparent bg-gradient-to-r from-purple-400 to-purple-600 p-[2px] shadow-lg hover:shadow-xl hover:scale-[1.02]"
                : "border-2 border-gray-200 bg-gray-50/30 opacity-60"
            )}
          >
            <div className={cn(
              "h-full rounded-[10px] overflow-hidden",
              packageCounts.setVideo > 0 ? "bg-white" : "bg-gray-50/30"
            )}>
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">SET Video</h3>
                  <p className="text-sm opacity-90">Video-Powered Partnership</p>
                </div>
                <div className="w-3 h-3 rounded-full bg-white"></div>
              </div>
              {packageCounts.setVideo > 0 && (
                <div className="mt-2 text-sm font-medium">
                  {packageCounts.setVideo} {packageCounts.setVideo === 1 ? 'show' : 'shows'} selected
                </div>
              )}
            </div>

            <div className="p-4 space-y-3">
              {DELIVERABLES.setVideo.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm hover:bg-purple-50/50 -mx-1 px-1 py-1 rounded transition-colors">
                  {item.included ? (
                    <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                  ) : (
                    <X className="h-4 w-4 text-gray-300 flex-shrink-0 mt-0.5" />
                  )}
                  <div className={cn("flex-1", !item.included && "text-gray-400")}>
                    <div className={item.included ? "font-medium" : ""}>{item.name}</div>
                    {item.note && (
                      <div className="text-xs text-purple-600 mt-0.5 font-medium">{item.note}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        </div>

        {/* Summary footer */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center text-sm">
            <div className="group hover:scale-105 transition-transform duration-200">
              <div className="text-3xl font-bold text-gray-600">{packageCounts.setCore}</div>
              <div className="text-muted-foreground font-medium">SET Core Shows</div>
            </div>
            <div className="group hover:scale-105 transition-transform duration-200">
              <div className="text-3xl font-bold text-blue-600">{packageCounts.setAudio}</div>
              <div className="text-muted-foreground font-medium">SET Audio Shows</div>
            </div>
            <div className="group hover:scale-105 transition-transform duration-200">
              <div className="text-3xl font-bold text-purple-600">{packageCounts.setVideo}</div>
              <div className="text-muted-foreground font-medium">SET Video Shows</div>
            </div>
          </div>
        </div>

        {/* Custom Package Section (if exists and has shows) */}
        {hasCustomShows && (() => {
          const customShowCount = Object.values(showPackages).filter(pkg => !pkg.setCore && !pkg.setAudio && !pkg.setVideo).length;

          // Case 1: No custom package configuration - show warning card
          if (!customPackage || !customPackage.selectedDeliverables || customPackage.selectedDeliverables.length === 0) {
            return (
              <div className="mt-6 pt-6 border-t border-amber-200">
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border-2 border-amber-400 p-6">
                  {/* Warning Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-amber-500 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-amber-700">Custom Package Configuration Missing</h3>
                      <p className="text-sm text-amber-600">
                        {customShowCount} show{customShowCount !== 1 ? 's' : ''} marked as custom but no package configured
                      </p>
                    </div>
                  </div>

                  {/* Warning Message */}
                  <div className="bg-white rounded-lg border border-amber-200 p-4 mb-4">
                    <p className="text-sm text-gray-700 mb-3">
                      You have shows selected with the <span className="font-semibold text-amber-700">Custom Package</span> tier,
                      but the custom package configuration has been cleared or not yet defined.
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">To resolve this:</span>
                    </p>
                    <ul className="text-sm text-gray-700 ml-6 mt-2 space-y-1">
                      <li>• Visit any show detail page and click <span className="font-semibold">"Create Custom Package"</span> to configure your deliverables</li>
                      <li>• Or change these shows to a different package tier (SET Core, Audio, or Video)</li>
                    </ul>
                  </div>

                  {/* Footer */}
                  <div className="text-center text-sm text-amber-600 font-medium">
                    Configure your custom package to see deliverables and complete your plan
                  </div>
                </div>
              </div>
            );
          }

          // Case 2: Has custom package configuration - show full card
          // Get base package deliverables based on selected tier
          const basePackageTier = customPackage.basePackageTier || 'set-core';
          const basePackageDeliverables = getBasePackageDeliverables(basePackageTier);
          const basePackageNames = basePackageDeliverables.map(d => d.name);

          // Get base package display info
          const basePackageInfo = getBasePackageInfo(basePackageTier);

          // Get all deliverable items for categorization
          const allDeliverableItems = PACKAGE_DELIVERABLES.filter(item =>
            customPackage.selectedDeliverables.includes(item.name)
          );

          // Group selected add-ons by category
          const selectedByCategory = groupDeliverablesByCategory(allDeliverableItems);

          // Determine which categories have selections
          const categoriesWithSelections = {
            mobile: selectedByCategory.mobile.length > 0,
            venue: selectedByCategory.venue.length > 0,
            media: selectedByCategory.media.length > 0,
          };

          const categoryCount = Object.values(categoriesWithSelections).filter(Boolean).length;

          return (
            <div className="mt-6 pt-6 border-t border-green-200">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-600 p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-green-600 flex items-center justify-center">
                      <Sliders className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-green-600">SET Custom</h3>
                      <p className="text-sm text-gray-600">Based on {basePackageInfo.name} • {customShowCount} show{customShowCount !== 1 ? 's' : ''} selected</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 font-medium">Pricing</div>
                    <div className="text-base font-bold text-green-600">Contact Us</div>
                  </div>
                </div>

                {/* Deliverables Layout - Mirror Modal Structure */}
                <div className="max-w-4xl mx-auto mb-6">
                  <div className="flex gap-6">
                    {/* Left Panel: Base Package (1/3 width) */}
                    <div className="w-1/3 border-r pr-4">
                      <div className="mb-3">
                        <h4 className="font-semibold text-sm mb-1 text-gray-700">
                          Starting with {basePackageInfo.name}
                        </h4>
                        <p className="text-xs text-gray-600">
                          {basePackageInfo.description}
                        </p>
                      </div>

                      <div className="space-y-2">
                        {basePackageDeliverables.map((item, idx) => {
                          // Get the appropriate note based on selected tier
                          const note = basePackageTier === 'set-core' ? item.setCore.note :
                                      basePackageTier === 'set-audio' ? item.setAudio.note :
                                      item.setVideo.note;

                          return (
                            <div key={idx} className="flex items-start gap-2 text-sm">
                              <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="h-3 w-3 text-white" />
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-gray-700 leading-tight">{item.name}</div>
                                {note && (
                                  <div className="text-xs text-gray-500 mt-0.5">{note}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Right Panel: Selected Add-ons (2/3 width) */}
                    <div className="flex-1">
                      {customPackage.selectedDeliverables.length > 0 ? (
                        <>
                          <div className="mb-3">
                            <h4 className="font-semibold text-sm mb-1 text-green-600">
                              Selected Upgrades
                            </h4>
                            <p className="text-xs text-gray-600">
                              {customPackage.selectedDeliverables.length} optional upgrade{customPackage.selectedDeliverables.length !== 1 ? 's' : ''} added
                            </p>
                          </div>

                          {/* Smart column layout based on category count */}
                          <div className={cn(
                            categoryCount === 1 ? "" : "grid grid-cols-2 gap-4"
                          )}>
                            {/* Column 1 or Single Column */}
                            <div className="space-y-3">
                              {/* Mobile Experience */}
                              {categoriesWithSelections.mobile && (
                                <div>
                                  <div className={cn(
                                    "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                                    CATEGORY_CONFIG.mobile.bgColor,
                                    CATEGORY_CONFIG.mobile.borderColor
                                  )}>
                                    <CATEGORY_CONFIG.mobile.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.mobile.textColor)} />
                                    <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.mobile.textColor)}>
                                      {CATEGORY_CONFIG.mobile.title}
                                    </h5>
                                  </div>
                                  <div className="space-y-1">
                                    {selectedByCategory.mobile.map((item, idx) => (
                                      <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                        <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                          <Check className="h-3 w-3 text-white" />
                                        </div>
                                        <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* In Show/Venue - Only in column 1 if: single category, OR 3 categories, OR 2 categories with media */}
                              {categoriesWithSelections.venue && (categoryCount !== 2 || categoriesWithSelections.media) && (
                                <div>
                                  <div className={cn(
                                    "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                                    CATEGORY_CONFIG.venue.bgColor,
                                    CATEGORY_CONFIG.venue.borderColor
                                  )}>
                                    <CATEGORY_CONFIG.venue.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.venue.textColor)} />
                                    <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.venue.textColor)}>
                                      {CATEGORY_CONFIG.venue.title}
                                    </h5>
                                  </div>
                                  <div className="space-y-1">
                                    {selectedByCategory.venue.map((item, idx) => (
                                      <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                        <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                          <Check className="h-3 w-3 text-white" />
                                        </div>
                                        <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Media - Only here if it's the only category OR if it's 2 categories (mobile + media or venue + media) */}
                              {categoriesWithSelections.media && categoryCount === 1 && (
                                <div>
                                  <div className={cn(
                                    "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                                    CATEGORY_CONFIG.media.bgColor,
                                    CATEGORY_CONFIG.media.borderColor
                                  )}>
                                    <CATEGORY_CONFIG.media.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.media.textColor)} />
                                    <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.media.textColor)}>
                                      {CATEGORY_CONFIG.media.title}
                                    </h5>
                                  </div>
                                  <div className="space-y-1">
                                    {selectedByCategory.media.map((item, idx) => (
                                      <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                        <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                          <Check className="h-3 w-3 text-white" />
                                        </div>
                                        <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Column 2 - Only if categoryCount > 1 */}
                            {categoryCount > 1 && (
                              <div className="space-y-3">
                                {/* Venue - In column 2 only if we have exactly 2 categories (mobile + venue) */}
                                {categoriesWithSelections.venue && categoryCount === 2 && !categoriesWithSelections.media && (
                                  <div>
                                    <div className={cn(
                                      "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                                      CATEGORY_CONFIG.venue.bgColor,
                                      CATEGORY_CONFIG.venue.borderColor
                                    )}>
                                      <CATEGORY_CONFIG.venue.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.venue.textColor)} />
                                      <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.venue.textColor)}>
                                        {CATEGORY_CONFIG.venue.title}
                                      </h5>
                                    </div>
                                    <div className="space-y-1">
                                      {selectedByCategory.venue.map((item, idx) => (
                                        <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                          <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Check className="h-3 w-3 text-white" />
                                          </div>
                                          <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Media - In column 2 for 2+ categories */}
                                {categoriesWithSelections.media && (
                                  <div>
                                    <div className={cn(
                                      "flex items-center gap-2 py-1 px-2 mb-1.5 rounded border-l-4",
                                      CATEGORY_CONFIG.media.bgColor,
                                      CATEGORY_CONFIG.media.borderColor
                                    )}>
                                      <CATEGORY_CONFIG.media.icon className={cn("w-3.5 h-3.5", CATEGORY_CONFIG.media.textColor)} />
                                      <h5 className={cn("text-xs font-semibold", CATEGORY_CONFIG.media.textColor)}>
                                        {CATEGORY_CONFIG.media.title}
                                      </h5>
                                    </div>
                                    <div className="space-y-1">
                                      {selectedByCategory.media.map((item, idx) => (
                                        <div key={idx} className="flex items-start gap-2 text-sm pl-2">
                                          <div className="h-4 w-4 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Check className="h-3 w-3 text-white" />
                                          </div>
                                          <div className="flex-1 font-medium text-gray-700 leading-tight">{item.name}</div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 italic">
                          No optional upgrades selected
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer Summary */}
                <div className="border-t border-green-200 pt-4">
                  <div className="text-sm text-gray-600 text-center">
                    <span className="font-medium">{basePackageNames.length + customPackage.selectedDeliverables.length}</span> deliverables included
                    <span className="text-gray-400 mx-2">•</span>
                    <span className="font-medium">{basePackageNames.length}</span> from {basePackageInfo.name} +{" "}
                    <span className="font-medium">{customPackage.selectedDeliverables.length}</span> upgrades
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}
